import * as moment from 'moment-timezone';
import * as React from 'react';
import JsonEditor from '../JsonEditor';
import { fetchLogs, ILog } from '../logic/requester';
import Popup from '../Popup';

import { downloadString } from '../logic/util';
import { generateCSV } from './csv';
import './style.css';

interface ITemplateStatistic {
  templateName: string;
  longestTime: number;
  fastestTime: number;
  averageTime: number;
  amount: number;
}

interface ILoggingState {
  logs?: ILog[];
  log?: ILog;
  search: string;
  page: number;
  stats?: ITemplateStatistic[];
}

class Logging extends React.Component<any, ILoggingState> {
  protected SUCCESS = <i className="far fa-smile" style={{ color: 'green' }} />;
  protected FAILURE = <i className="far fa-frown" style={{ color: 'red' }} />;
  constructor(props: any, context: any) {
    super(props, context);
    moment.tz.setDefault(moment.tz.guess()); // Get the timezone of the browser.
    this.state = { search: '', page: 0 };
    this.fetch = this.fetch.bind(this);
  }

  public fetch(serverUrl?: string) {
    this.setState({ logs: undefined, log: undefined });
    fetchLogs(serverUrl).then(logs => {
      this.setState({ logs });
    }).catch(console.error); // TODO: ( handle error stuff )
  }

  public downloadLog() {
    if (!this.state.logs) return alert('No logs fetched yet');
    downloadString(JSON.stringify(this.state.logs, undefined, 4), 'log', 'json');
  }

  public genStats() {
    const statsMap: { [templateName: string]: ITemplateStatistic } = {};
    for (const log of (this.state.logs || [])) {
      const { template_name, template_type } = log;
      if (!template_name) continue;
      const templateName = `${template_name}.${template_type}`;
      const startTime = moment(log.start_time);
      const endTime = moment(log.end_time);
      const duration = moment.duration(endTime.diff(startTime)).asMilliseconds();
      const stat: ITemplateStatistic = statsMap[template_name];
      if (!stat) {
        statsMap[template_name] = {
          templateName,
          amount: 1,
          averageTime: duration,
          fastestTime: duration,
          longestTime: duration,
        };
        continue;
      }
      stat.averageTime = (stat.averageTime * stat.amount + duration) / (stat.amount + 1);
      stat.fastestTime = duration < stat.fastestTime ? duration : stat.fastestTime;
      stat.longestTime = duration > stat.longestTime ? duration : stat.longestTime;
      stat.amount += 1;
    }
    const stats: ITemplateStatistic[] = [];
    // tslint:disable-next-line:forin
    for (const key in statsMap) {
      stats.push(statsMap[key]);
    }
    return stats;
  }

  public downloadCSV() {
    if (!this.state.logs) return alert('No logs fetched yet');
    const csv = generateCSV(this.state.logs);
    downloadString(csv, 'log', 'csv');
  }

  public showStatistics() {
    if (!this.state.logs) return alert('No logs fetched yet');
    this.setState({ stats: this.genStats() });
  }

  public getFilteredLogs() {
    const { logs } = this.state;
    if (!logs || !logs.length) return null;
    const keys: string[] = ['start_time', 'end_time', 'template_type', 'output_type', 'urid']; // Keys to filter on in `log`
    const ldKeys: string[] = ['user', 'session']; // Keys to filter on in `log.logging_data`
    const search = this.state.search;
    let match: RegExp | null;
    try {
      match = new RegExp(search);
    } catch (e) {
      match = null;
    }
    return logs.filter(log => {
      return keys.some(key => log[key] && (match && !!log[key].match(match) || log[key].includes(search)))
        || ldKeys.some(key => log.logging_data[key] && (match && !!log.logging_data[key].match(match) || log.logging_data[key].includes(search)));
    });
  }

  public render() {
    const logSet = this.getFilteredLogs();
    if (!logSet) return <div className="Logging"><p>No logs are fetched yet...</p></div>;
    const pages = Math.ceil(logSet.length / 25);
    const page = Math.min(pages - 1, this.state.page);
    const shownLogs = logSet.reverse().slice(page * 25, (page + 1) * 25);
    const prevPage = () => this.setState({ page: page - 1 });
    const nextPage = () => this.setState({ page: page + 1 });
    console.log(pages, page);
    return (
      <div className="Logging">
        <label htmlFor="logging-search" />
        <input id="logging-search" value={this.state.search} onChange={this.setSearch} placeholder="Search" />
        <div style={{ float: 'right' }}>
          <button onClick={prevPage} disabled={page === 0}>{'<'}</button>
          <select value={page} onChange={this.setPage}>{this.createPages(pages)}</select>
          <button onClick={nextPage} disabled={page === (pages - 1)}>{'>'}</button>
        </div>
        <table className="logging">
          <thead>
            <tr>
              <th className="right">Start Time</th>
              <th className="right">End Time</th>
              <th>User</th>
              <th>Session</th>
              <th className="right">Duration</th>
              <th className="right">JSON size</th>
              <th className="right">Output size</th>
              <th>Template</th>
              <th>Output</th>
              <th>Success</th>
              <th>Debug</th>
            </tr>
          </thead>
          <tbody>
            {
              shownLogs.map((log) => {
                const startTime = moment(log.start_time);
                const endTime = moment(log.end_time);
                const timeDiff = moment.duration(endTime.diff(startTime));

                let template = log.template_name || log.logging_data.template_filename;
                if (!template) {
                  template = log.template_type;
                } else if (!template.match(/\./)) {
                  template = `${template}.${log.template_type}`;
                }

                let output = log.logging_data.output_filename;
                if (!output) {
                  output = log.output_type;
                } else if (!output.match(/\./)) {
                  output = `${output}.${log.output_type}`;
                }

                return <tr key={log.id}>
                  <td className="right">{startTime.format('D MMM YYYY HH:mm:ss.SSS')}</td>
                  <td className="right">{endTime.format('D MMM YYYY HH:mm:ss.SSS')}</td>
                  <td>{log.logging_data.user || ''}</td>
                  <td>{log.logging_data.session || ''}</td>
                  <td className="right">{timeDiff + ' ms'}</td>
                  <td className="right">{log.json_size ? `${Math.floor(log.json_size / 1024)} kb` : 'N/A'}</td>
                  <td className="right">{log.output_size ? `${Math.floor(log.output_size / 1024)} kb` : 'N/A'}</td>
                  <td>{template || ''}</td>
                  <td>{output || ''}</td>
                  <td>{log.success ? this.SUCCESS : this.FAILURE}</td>
                  <td><button className="debug" onClick={this.debugButton(log)}>Debug</button></td>
                </tr>
              })
            }
          </tbody>
        </table>
        {this.state.log && <Popup visible={true} close={this.closePopup} width="80%" height="80%">
          <JsonEditor json={this.state.log} readOnly={true} name={this.state.log.urid} />
        </Popup>}
        {this.state.stats && <Popup visible={true} close={this.closePopup} width="80%" height="80%">
          <table className="logging">
            <thead>
              <tr>
                <th>Template name</th>
                <th className="right">Amount</th>
                <th className="right">Average duration</th>
                <th className="right">Fastest duration</th>
                <th className="right">Longest duration</th>
              </tr>
            </thead>
            <tbody>
              {
                this.state.stats.map((stat) => {
                  return <tr key={stat.templateName}>
                    <td>{stat.templateName}</td>
                    <td className="right">{stat.amount}</td>
                    <td className="right">{(Math.round(stat.averageTime * 1000) / 1000) + ' ms'}</td>
                    <td className="right">{(Math.round(stat.fastestTime * 1000) / 1000) + ' ms'}</td>
                    <td className="right">{(Math.round(stat.longestTime * 1000) / 1000) + ' ms'}</td>
                  </tr>
                })
              }
            </tbody>
          </table>
        </Popup>}
      </div>
    );
  }
  protected createPages(pages: number) {
    const res: JSX.Element[] = [];
    for (let page = 0; page < pages; page += 1) {
      res.push(<option key={page} value={page}>{page + 1}</option>);
    }
    return res;
  }
  protected setSearch = (event: React.ChangeEvent<HTMLInputElement>) => this.setState({ search: event.target.value });
  protected setPage = (event: React.ChangeEvent<HTMLSelectElement>) => this.setState({ page: Number(event.target.value) });
  protected debugButton = (log: any) => () => this.setState({ log });
  protected closePopup = () => this.setState({ log: undefined, stats: undefined });
}

export default Logging;



// WEBPACK FOOTER //
// ./src/Logging/index.tsx